import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Tooltip,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountTree as HierarchyIcon,
  Security as SecurityIcon,
  People as PeopleIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { HierarchyLevel, HierarchyLevelInput } from '../types';
import hierarchyLevelService from '../services/hierarchyLevelService';

interface DialogState {
  open: boolean;
  mode: 'create' | 'edit';
  hierarchyLevel?: HierarchyLevel;
}

const HierarchyLevels: React.FC = () => {
  const [hierarchyLevels, setHierarchyLevels] = useState<HierarchyLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<DialogState>({ open: false, mode: 'create' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form state
  const [formData, setFormData] = useState<HierarchyLevelInput>({
    hierarchyLevelId: 1,
    name: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    loadHierarchyLevels();
  }, []);

  const loadHierarchyLevels = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await hierarchyLevelService.getHierarchyLevels();
      setHierarchyLevels(data || []);
    } catch (err: any) {
      console.error('Error loading hierarchy levels:', err);
      setError(err.message || 'Failed to load hierarchy levels');
    } finally {
      setLoading(false);
    }
  };

  const loadNextLevelId = async () => {
    try {
      const id = await hierarchyLevelService.getNextHierarchyLevelId();
      return id;
    } catch (err: any) {
      console.error('Error loading next level ID:', err);
      return 1;
    }
  };

  const handleOpenDialog = async (mode: 'create' | 'edit', hierarchyLevel?: HierarchyLevel) => {
    if (mode === 'create') {
      const nextId = await loadNextLevelId();
      setFormData({
        hierarchyLevelId: nextId,
        name: '',
        description: '',
        isActive: true
      });
    } else if (hierarchyLevel) {
      setFormData({
        hierarchyLevelId: hierarchyLevel.hierarchyLevelId,
        name: hierarchyLevel.name,
        description: hierarchyLevel.description || '',
        isActive: hierarchyLevel.isActive
      });
    }
    
    setDialogState({ open: true, mode, hierarchyLevel });
  };

  const handleCloseDialog = () => {
    setDialogState({ open: false, mode: 'create' });
    setFormData({
      hierarchyLevelId: 1,
      name: '',
      description: '',
      isActive: true
    });
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      
      if (dialogState.mode === 'create') {
        await hierarchyLevelService.createHierarchyLevel(formData);
        setSuccess('Hierarchy level created successfully!');
      } else if (dialogState.hierarchyLevel) {
        await hierarchyLevelService.updateHierarchyLevel(dialogState.hierarchyLevel.id, formData);
        setSuccess('Hierarchy level updated successfully!');
      }
      
      handleCloseDialog();
      await loadHierarchyLevels();
    } catch (err: any) {
      console.error('Error saving hierarchy level:', err);
      setError(err.message || 'Failed to save hierarchy level');
    }
  };

  const handleDelete = async (hierarchyLevel: HierarchyLevel) => {
    if (!window.confirm(`Are you sure you want to delete "${hierarchyLevel.name}"?`)) {
      return;
    }

    try {
      setError(null);
      
      // Delete the hierarchy level directly (no pre-validation needed)
      await hierarchyLevelService.deleteHierarchyLevel(hierarchyLevel.id);
      setSuccess('Hierarchy level deleted successfully!');
      await loadHierarchyLevels();
    } catch (err: any) {
      console.error('Error deleting hierarchy level:', err);
      setError(err.message || 'Failed to delete hierarchy level');
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getTypeIcon = (hierarchyLevel: HierarchyLevel) => {
    if (hierarchyLevel.isSystemLevel) {
      return <SecurityIcon color="error" />;
    } else if (hierarchyLevel.isUserLevel) {
      return <PeopleIcon color="primary" />;
    } else {
      return <SettingsIcon color="info" />;
    }
  };

  const getTypeLabel = (hierarchyLevel: HierarchyLevel) => {
    if (hierarchyLevel.isSystemLevel) {
      return 'System Level';
    } else if (hierarchyLevel.isUserLevel) {
      return 'User Level';
    } else {
      return 'Standard Level';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Hierarchy Levels
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage hierarchical levels in the PECS system
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('create')}
          sx={{ borderRadius: 2 }}
        >
          Add Hierarchy Level
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'primary.light',
                    color: 'primary.main',
                    mr: 2
                  }}
                >
                  <HierarchyIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {hierarchyLevels.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Levels
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'success.light',
                    color: 'success.main',
                    mr: 2
                  }}
                >
                  <HierarchyIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {hierarchyLevels.filter(hl => hl.isActive).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Levels
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'error.light',
                    color: 'error.main',
                    mr: 2
                  }}
                >
                  <SecurityIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {hierarchyLevels.filter(hl => hl.isSystemLevel).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    System Levels
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'info.light',
                    color: 'info.main',
                    mr: 2
                  }}
                >
                  <PeopleIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {hierarchyLevels.filter(hl => hl.isUserLevel).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    User Levels
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Hierarchy Levels Table */}
      <Paper elevation={2}>
        <Box sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Level ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hierarchyLevels
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((hierarchyLevel) => (
                        <TableRow key={hierarchyLevel.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {hierarchyLevel.hierarchyLevelId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {getTypeIcon(hierarchyLevel)}
                              <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                                {hierarchyLevel.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {hierarchyLevel.description || 'No description'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getTypeLabel(hierarchyLevel)}
                              size="small"
                              color={hierarchyLevel.isSystemLevel ? 'error' : hierarchyLevel.isUserLevel ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={hierarchyLevel.isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={hierarchyLevel.isActive ? 'success' : 'default'}
                              variant="filled"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(hierarchyLevel.createdDate).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog('edit', hierarchyLevel)}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(hierarchyLevel)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={hierarchyLevels.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Box>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogState.open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogState.mode === 'create' ? 'Create Hierarchy Level' : 'Edit Hierarchy Level'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Hierarchy Level ID"
                  type="number"
                  fullWidth
                  value={formData.hierarchyLevelId}
                  onChange={(e) => setFormData({ ...formData, hierarchyLevelId: parseInt(e.target.value) || 1 })}
                  helperText="Unique numeric identifier for this level"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  helperText="Name of the hierarchy level"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  helperText="Optional description of the hierarchy level"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name.trim()}
          >
            {dialogState.mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HierarchyLevels;
